import { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { VpnKey } from "@mui/icons-material";
import AppDialog from "components/common/AppDialog";
import { useGenerateEncryptedSftpKeyPairMutation } from "features/sftp/sftp";
import {
  KEY_ALGORITHM_OPTIONS,
  type KeyAlgorithm,
  downloadSshKeyBundleAsZip,
} from "./sshKeyGen";
import { errorDetailFromUnknown } from "./sftpKeyPairApiErrors";

interface KeyTypePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (publicKeyOpenSSH: string) => void;
  username: string;
  onError?: (message: string) => void;
}

export default function KeyTypePickerDialog({
  open,
  onClose,
  onGenerated,
  username,
  onError,
}: KeyTypePickerDialogProps) {
  const [selected, setSelected] = useState<KeyAlgorithm>("rsa-4096");
  const [generatePair, { isLoading: isGenerating }] =
    useGenerateEncryptedSftpKeyPairMutation();

  const handleGenerate = async () => {
    const passphrase = (username || "").trim();
    if (!passphrase) {
      onError?.("Set a username first; it is used as the key passphrase.");
      return;
    }
    try {
      const data = await generatePair({
        algorithm: selected === "ed25519" ? "ed25519" : "rsa-4096",
        passphrase,
      }).unwrap();
      try {
        await downloadSshKeyBundleAsZip(
          data.private_key,
          data.public_key.trim(),
          passphrase,
          selected,
          passphrase,
        );
      } catch {
        onError?.(
          "Keys were generated but the ZIP archive could not be created.",
        );
        return;
      }
      onGenerated(data.public_key.trim());
      onClose();
    } catch (e) {
      onError?.(errorDetailFromUnknown(e) ?? "Failed to generate key pair.");
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={isGenerating ? () => {} : onClose}
      title="Generate Key Pair"
      maxWidth="xs"
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={isGenerating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isGenerating}
            startIcon={
              isGenerating ? <CircularProgress size={16} /> : <VpnKey />
            }
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick a key type below. You get one ZIP with your private key, public key
        (.pub), and a short reminder file. Your SFTP username (included in the
        txt file) is the passphrase - enter it when your SFTP app asks for the
        key passphrase.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {KEY_ALGORITHM_OPTIONS.map((opt) => {
          const active = selected === opt.id;
          return (
            <Box
              key={opt.id}
              onClick={() => !isGenerating && setSelected(opt.id)}
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 1,
                border: "2px solid",
                borderColor: active ? "primary.main" : "divider",
                bgcolor: active ? "calibrate.primarySoft05" : "transparent",
                cursor: isGenerating ? "default" : "pointer",
                transition: "all 0.15s",
                "&:hover": isGenerating
                  ? {}
                  : {
                      borderColor: "primary.main",
                      bgcolor: "calibrate.primarySoft03",
                    },
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {opt.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {opt.description}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </AppDialog>
  );
}
